import autoComplete from "@tarekraafat/autocomplete.js";
import DOMPurify from 'dompurify';

const searchInput = document.querySelector("#autoComplete");

if (searchInput) {

  let isInteractingWithResults = false;

  // Track interactions on search components
  document.addEventListener('touchstart', (e) => {
    isInteractingWithResults = !!e.target.closest('#autoComplete_list, .autoComplete_wrapper, #autoComplete');
  }, { passive: true });

  document.addEventListener('mousedown', (e) => {
    isInteractingWithResults = !!e.target.closest('#autoComplete_list, .autoComplete_wrapper, #autoComplete');
  });

  let hasSearched = false;

  const autoCompleteJS = new autoComplete({
    selector: "#autoComplete",
    placeHolder: "Search Keyword or Brand...",
    threshold: 3,
    debounce: 1000,
    submit: true,

    query: (input) => input,

    // Bypasses client-side matching so 100% of backend items pass through
    searchEngine: (query, record) => record,

    data: {
      src: async (query) => {
        try {
          const cleanQuery = DOMPurify.sanitize(query);
          const response = await fetch(`/ajax/search?q=${encodeURIComponent(cleanQuery)}`);
          if (!response.ok) return [];
          const data = await response.json();
          hasSearched = true;
          return data;
        } catch (error) {
          console.error("Search fetch error:", error);
          return [];
        }
      },
      keys: ["name", "value"],
    },

    resultsList: {
      maxResults: 10,
      noResults: true,
      element: (list, data) => {

        if (!data.results.length) {
          const message = document.createElement("div");
          message.className = "empty-message";
          message.style.cssText = "padding: 10px;";
          message.innerHTML = `Unable to find any products matching "${DOMPurify.sanitize(data.query)}".`;
          list.prepend(message);
          return;
        }

        if (data.matches.length >= 10) {
          const viewMoreContainer = document.createElement("div");
          viewMoreContainer.className = "autoComplete-view-more";

          const viewMoreLink = document.createElement("a");
          viewMoreLink.href = `/search?searchField=${encodeURIComponent(data.query)}`;
          viewMoreLink.innerHTML = `View all results for "${DOMPurify.sanitize(data.query)}" <i class="far fa-arrow-right"></i>`;

          viewMoreContainer.appendChild(viewMoreLink);
          list.appendChild(viewMoreContainer);
        }
      },
    },

    resultItem: {
      element: (item, data) => {
        const product = data.value;
        const imgId = product.imgId;

        // Image HTML
        const imgTag = (imgId && imgId !== "0")
          ? `<img src="https://www.applianceelectronics.co.uk/product_pics/${DOMPurify.sanitize(imgId)}-thumb.jpg" class="img-responsive" style="max-width: 50px;" />`
          : '';

        // Price handling
        const rawPrice = product.price ?? '';
        const displayPrice = (typeof rawPrice === 'number') 
          ? `£${rawPrice.toFixed(2)}` 
          : DOMPurify.sanitize(String(rawPrice));

        // Get sanitized product title
        const rawTitle = DOMPurify.sanitize(product.name || product.value || '');
        
        // Grab search string directly from the input element
        const searchQuery = DOMPurify.sanitize(searchInput.value || '').trim();

        // Highlight matching query text inside the title (case-insensitive)
        let highlightedTitle = rawTitle;
        if (searchQuery.length > 0) {
          searchQuery.split(/\s+/).forEach((word) => {
            if (!word) return;
            // Escape regex characters ONLY when instantiating RegExp
            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const pattern = new RegExp(escapedWord, 'gi');

            // Use a match function parameter ($0) to preserve the actual matched text case and characters
            highlightedTitle = highlightedTitle.replace(pattern, (match) => `<mark>${match}</mark>`);
          }); 
        }

        item.innerHTML = `
          <div class="search-item">
            <div class="search-image">${imgTag}</div>
            <div class="search-name">${highlightedTitle}</div>
            <div class="search-price">${displayPrice}</div>
          </div>
        `;
      },
    },

    events: {
      input: {
        focus() {
          const inputValue = autoCompleteJS.input.value.trim();

          if (!inputValue.length) return;

          if (hasSearched) {
            autoCompleteJS.open();
          } else {
            autoCompleteJS.start();
          }
        },

        blur() {
          setTimeout(() => {
            // Close if user clicked/tapped outside search box & dropdown list
            if (!isInteractingWithResults && document.activeElement !== searchInput) {
              autoCompleteJS.close();
            }
          }, 150);
        },       

        selection: (event) => {
          const selectedProduct = event.detail.selection.value;
          if (selectedProduct && selectedProduct.url) {
            window.location.href = selectedProduct.url;
          }
        },
      },
    },
  });

  // Touch & Scroll listener for mobile and tablet devices (< 1024px)
  let startY = 0;
  let startPageY = 0;
  let isScrollOnMenu = false;

  const isTouchDevice = () => {
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    return hasTouch && window.innerWidth < 1024;
  };

  document.addEventListener('touchstart', (e) => {
    if (!isTouchDevice()) return;

    isScrollOnMenu = !!e.target.closest('#autoComplete_list, .autoComplete_wrapper, #autoComplete');
    startY = e.touches[0].clientY;
    startPageY = window.scrollY;
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (!isTouchDevice()) return;

    if (isScrollOnMenu) return;

    const currentY = e.touches[0].clientY;
    const touchDiff = Math.abs(currentY - startY);
    const scrollDiff = Math.abs(window.scrollY - startPageY);

    if (touchDiff > 15 && scrollDiff > 10) {
      autoCompleteJS.close();
      searchInput.blur();
    }
  }, { passive: true });
}